import React, { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import styled, { createGlobalStyle } from 'styled-components'

import { BannerInfoTypes } from '@/common/types/BannerInfo'
import getColorVar from '@/common/utils/getColorVar'
import { useSensitiveDataToggle } from '@/common/hooks/useSensitiveDataToggle'
import Button from '@/common/components/Button'
import useComponentSize from '@/common/hooks/useComponentSize'

const WarnBannerStyles = createGlobalStyle<{ borderColor: string | undefined; bannerHeight: number }>`
  #root {
    border: ${({ borderColor }) => (!!borderColor ? `2px solid ${borderColor}` : 'none')};
  }

  [data-is-info-banner-active] {
    padding-top: ${({ bannerHeight }) => (bannerHeight ? bannerHeight : 32)}px;
  }
`

export const InformationBanner = () => {
  const [bannerInfoByEnvironment, setBannerInfoByEnvironment] = useState<BannerInfoTypes | null>(null)

  const { t } = useTranslation('common')
  const { globalSensitiveDataStyles, hideInfo, setHideInfo } = useSensitiveDataToggle()
  const [ref, size] = useComponentSize<HTMLDivElement>({ deps: [bannerInfoByEnvironment, hideInfo] })

  useEffect(() => {
    if (window?.App?.bannerInfo && window?.App?.bannerInfo?.text && !bannerInfoByEnvironment) {
      const { text, color } = window.App.bannerInfo

      setBannerInfoByEnvironment({
        text,
        borderColor: getColorVar(color),
        backgroundColor: getColorVar(color, 200),
      })
    }
  }, [window])

  if (!bannerInfoByEnvironment && !hideInfo) return null

  return (
    <>
      {globalSensitiveDataStyles}
      <WarnBannerStyles borderColor={bannerInfoByEnvironment?.borderColor} bannerHeight={size.height} />
      <InformationBannerContainer
        ref={ref}
        borderColor={bannerInfoByEnvironment?.borderColor}
        bgColor={bannerInfoByEnvironment?.backgroundColor}
      >
        {!!bannerInfoByEnvironment?.text && (
          <div className="env-info" dangerouslySetInnerHTML={{ __html: bannerInfoByEnvironment?.text }} />
        )}
        {hideInfo && (
          <SensitiveDataContainer>
            <strong>{t('attentionAlertBanner')}</strong>
            <span>{t('descriptionAlertBanner')}</span>
            <Button variant="link" className="disable-sensitive-mode" onClick={() => setHideInfo(false)}>
              {t('disable')}
            </Button>
          </SensitiveDataContainer>
        )}
      </InformationBannerContainer>
    </>
  )
}

const InformationBannerContainer = styled.div<{ bgColor: string | undefined; borderColor: string | undefined }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;

  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;

  padding: 4px 0;
  min-height: 32px;

  border: ${({ borderColor }) => (!!borderColor ? `2px solid ${borderColor}` : 'none')};
  border-bottom: none;
  color: var(--gray-800);
  background-color: ${({ bgColor }) => (!!bgColor ? bgColor : 'var(--blue-200)')};

  .env-info {
    margin-right: 8px;
    color: var(--blueish-800);
  }
`

const SensitiveDataContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  padding: 4px 8px;
  border-radius: 4px;

  border: 1px solid var(--blueish-600);
  background-color: var(--gray-0);

  strong {
    margin-right: 4px;
  }

  strong,
  span {
    font-size: 10px;
    line-height: 10px;
  }

  .disable-sensitive-mode {
    position: absolute;
    right: 24px;

    font-size: 12px;
  }
`
